import React, {useEffect, useState} from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Styles from "./styles.module.scss";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";

import capsuleImg from '../../../theme/assets/other/tc_cab_apostol_capsule.png';
import tcIcon from '../../../theme/assets/other/tc_crypto_tc_icon.png';
import infoIcon from '../../../theme/assets/svg/tc_cab_info_icon.svg';
import successIcon from '../../../theme/assets/svg/tc_cab_capsule_success_icon.svg';
import errorIcon from '../../../theme/assets/svg/tc_cab_capsule_error_icon.svg';

import {
    getCapsuleOffer,
    getCapsuleStats,
    getUserData,
    purchaseCapsule,
    purchaseCapsuleFailure,
    setCapsuleBuyModalStatus,
    setTopUpModalStatus
} from "../../../store/cabinet/action";
import {amountHandler, dateTimeHandler} from "../../../helpers/capsule";

// Внутренний баланс приходит без валюты — тикер берём из макета
const CAPSULE_CURRENCY = '$TC';

const CabinetCapsuleBuyModal = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const capsuleBuyModalStatus = useSelector(store => store.cabinet.capsuleBuyModalStatus);
    const capsuleOffer = useSelector(store => store.cabinet.capsuleOffer);
    const purchaseCapsuleStarted = useSelector(store => store.cabinet.purchaseCapsuleStarted);
    const purchaseCapsuleStatus = useSelector(store => store.cabinet.purchaseCapsuleStatus);
    const purchaseErr = useSelector(store => store.cabinet.purchaseCapsuleErr);

    // form — подтверждение покупки, success / error — результат оплаты
    const [view, setView] = useState('form');
    const [failedAt, setFailedAt] = useState(false);

    const price = capsuleOffer?.price;
    const balance = capsuleOffer?.balance;
    const isAffordable = !!capsuleOffer?.affordable;
    const isPurchasable = !!capsuleOffer?.purchasable;

    useEffect(()=>{
        if (capsuleBuyModalStatus){
            dispatch(getCapsuleOffer());
        }
    },[capsuleBuyModalStatus, dispatch])

    const closeModal = () =>{
        const wasPurchased = view === 'success';

        setView('form');
        setFailedAt(false);
        dispatch(purchaseCapsuleFailure(false));
        dispatch(setCapsuleBuyModalStatus(false));

        // страховка: если запрос после оплаты не прошёл, страница всё равно
        // должна открыться без перезагрузки
        if (wasPurchased){
            dispatch(getCapsuleStats());
            dispatch(getUserData());
        }
    }

    const topUpHandler = () =>{
        dispatch(setCapsuleBuyModalStatus(false));
        // пополнение живёт на главной кабинета — открываем её сразу с модалкой
        navigate('/cabinet');
        dispatch(setTopUpModalStatus(true));
    }

    const purchaseHandler = () =>{
        if (purchaseCapsuleStarted || !isAffordable || !isPurchasable){
            return;
        }

        dispatch(purchaseCapsule(
            ()=>{
                setView('success');
                // разблокируем страницу сразу: модалка смонтирована независимо
                // от статуса капсулы, поэтому чек остаётся на экране
                dispatch(getCapsuleStats());
                dispatch(getUserData());
            },
            ()=>{
                setFailedAt(new Date().toISOString());
                setView('error');
            }
        ));
    }

    const retryHandler = () =>{
        dispatch(purchaseCapsuleFailure(false));
        setFailedAt(false);
        setView('form');
        dispatch(getCapsuleOffer());
    }

    const errorText = purchaseErr?.errors?.balance?.[0]
        || purchaseErr?.errors?.capsule?.[0]
        || purchaseErr?.message
        || t('Средства не списаны, баланс не изменился. Проверьте соединение и повторите оплату.');

    return (
        <Popup
            open={capsuleBuyModalStatus}
            modal
            nested
            className="capsuleModal"
            closeOnDocumentClick
            onClose={closeModal}
        >
            <div className={`${Styles.modalContainer}`}>
                <div className={`${Styles.modalCloseBtn}`} onClick={closeModal}>
                    <div/>
                    <div/>
                </div>

                {view === 'form' ? (
                    <div className={`${Styles.modalContentWrap}`}>
                        <div className={`${Styles.modalHead}`}>
                            <div className={`${Styles.modalTitle}`}>{t('Покупка капсулы')}</div>

                            {capsuleOffer && !isAffordable ? (
                                <div className={`${Styles.warningWrap}`}>
                                    <img src={infoIcon} alt=""/>
                                    <div className={`${Styles.warningPos}`}>
                                        <div className={`${Styles.warningTitle}`}>{t('Недостаточно средств')}</div>
                                        <div className={`${Styles.warningNote}`}>
                                            {t('Пополните баланс до')} {amountHandler(price)} {CAPSULE_CURRENCY}, {t('чтобы купить капсулу')}
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            <div className={`${Styles.modalNote}`}>
                                {t('Проверьте сумму — она спишется с вашего внутреннего баланса')}
                            </div>
                        </div>

                        <div className={`${Styles.modalBody}`}>
                            <div className={`${Styles.productWrap}`}>
                                <div className={`${Styles.productImgWrap}`}>
                                    <img src={capsuleImg} alt={t('Персональная капсула')}/>
                                </div>
                                <div className={`${Styles.productPos}`}>
                                    <div className={`${Styles.productTitle}`}>{t('Персональная капсула')}</div>
                                    <div className={`${Styles.productNote}`}>
                                        {t('Записи, файлы и воспоминания, которые вы оставите после себя')}
                                    </div>
                                    <div className={`${Styles.productPriceName}`}>{t('Стоимость')}:</div>
                                    <div className={`${Styles.productPriceRow}`}>
                                        <img src={tcIcon} alt=""/>
                                        <span>{amountHandler(price)} {CAPSULE_CURRENCY}</span>
                                    </div>
                                </div>
                            </div>

                            <div className={`${Styles.summaryWrap}`}>
                                <div className={`${Styles.summaryRow}`}>
                                    <span>{t('Ваш баланс')}</span>
                                    <span className={`${capsuleOffer && !isAffordable ? Styles.summaryValueErr : Styles.summaryValue}`}>
                                        {amountHandler(balance)} {CAPSULE_CURRENCY}
                                    </span>
                                </div>
                                <div className={`${Styles.summaryDivider}`}/>
                                <div className={`${Styles.summaryRow}`}>
                                    <span>{t('Стоимость капсулы')}</span>
                                    <span className={`${Styles.summaryValue}`}>{amountHandler(price)} {CAPSULE_CURRENCY}</span>
                                </div>
                            </div>
                        </div>

                        <div className={`${Styles.modalFooter}`}>
                            <div className={`${Styles.modalBtnGhost}`} onClick={closeModal}>{t('Отмена')}</div>
                            {capsuleOffer && !isAffordable ? (
                                <div className={`${Styles.modalBtn} ${Styles.modalBtnWarning}`} onClick={topUpHandler}>
                                    {t('Пополнить баланс')}
                                </div>
                            ) : (
                                <div
                                    className={`${Styles.modalBtn} ${!capsuleOffer || !isPurchasable || purchaseCapsuleStarted ? Styles.modalBtnDsb : ''}`}
                                    onClick={purchaseHandler}
                                >
                                    {purchaseCapsuleStarted
                                        ? t('Оплачиваем…')
                                        : `${t('Оплатить')} ${amountHandler(price)} ${CAPSULE_CURRENCY}`}
                                </div>
                            )}
                        </div>
                    </div>
                ) : null}

                {view === 'success' ? (
                    <div className={`${Styles.modalContentWrap}`}>
                        <div className={`${Styles.modalHead}`}>
                            <div className={`${Styles.modalTitle}`}>{t('Покупка капсулы')}</div>
                        </div>

                        <div className={`${Styles.modalBody}`}>
                            <div className={`${Styles.resultWrap}`}>
                                <div className={`${Styles.resultIconWrap}`}>
                                    <img src={successIcon} alt=""/>
                                </div>
                                <div className={`${Styles.resultTitle}`}>{t('Капсула куплена')}</div>
                                <div className={`${Styles.resultNote}`}>
                                    {t('Капсула уже доступна — добавляйте записи, файлы и воспоминания')}
                                </div>
                            </div>

                            <div className={`${Styles.summaryWrap}`}>
                                <div className={`${Styles.summaryRow}`}>
                                    <span>{t('Списано с баланса')}</span>
                                    <span className={`${Styles.summaryValue}`}>
                                        -{amountHandler(purchaseCapsuleStatus?.transaction?.amount)} {CAPSULE_CURRENCY}
                                    </span>
                                </div>
                                <div className={`${Styles.summaryDivider}`}/>
                                <div className={`${Styles.summaryRow}`}>
                                    <span>{t('Дата и время')}</span>
                                    <span>{dateTimeHandler(purchaseCapsuleStatus?.transaction?.created_at || purchaseCapsuleStatus?.capsule_opened_at)}</span>
                                </div>
                            </div>
                        </div>

                        <div className={`${Styles.modalFooter} ${Styles.modalFooterSingle}`}>
                            <div className={`${Styles.modalBtn}`} onClick={closeModal}>{t('Закрыть')}</div>
                        </div>
                    </div>
                ) : null}

                {view === 'error' ? (
                    <div className={`${Styles.modalContentWrap}`}>
                        <div className={`${Styles.modalHead}`}>
                            <div className={`${Styles.modalTitle}`}>{t('Покупка капсулы')}</div>
                        </div>

                        <div className={`${Styles.modalBody}`}>
                            <div className={`${Styles.resultWrap}`}>
                                <div className={`${Styles.resultIconWrap}`}>
                                    <img src={errorIcon} alt=""/>
                                </div>
                                <div className={`${Styles.resultTitle} ${Styles.resultTitleErr}`}>{t('Оплата не прошла')}</div>
                                <div className={`${Styles.resultNote}`}>{errorText}</div>
                            </div>

                            <div className={`${Styles.summaryWrap}`}>
                                <div className={`${Styles.summaryRow}`}>
                                    <span>{t('Сумма покупки')}</span>
                                    <span className={`${Styles.summaryValueErr}`}>{amountHandler(price)} {CAPSULE_CURRENCY}</span>
                                </div>
                                <div className={`${Styles.summaryDivider}`}/>
                                <div className={`${Styles.summaryRow}`}>
                                    <span>{t('Дата и время')}</span>
                                    <span>{dateTimeHandler(failedAt)}</span>
                                </div>
                            </div>
                        </div>

                        <div className={`${Styles.modalFooter} ${Styles.modalFooterSingle}`}>
                            <div className={`${Styles.modalBtn}`} onClick={retryHandler}>{t('Повторить оплату')}</div>
                        </div>
                    </div>
                ) : null}
            </div>
        </Popup>
    );
};

export default CabinetCapsuleBuyModal;
